import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  OnInit,
  Input,
} from '@angular/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { IttWrapperComponent } from '../itt-wrapper/itt-wrapper.component';
import { Router } from '@angular/router';
import { TenderingService } from '@/app/services/tendering-services/tendering.service';
import { NgToastService } from 'ng-angular-popup';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-itt-technical-evaluation-weightage',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    MatButtonModule,
    MatSelectModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDatepickerModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './itt-technical-evaluation-weightage.component.html',
  styleUrl: './itt-technical-evaluation-weightage.component.css',
})
export class IttTechnicalEvaluationWeightageComponent implements OnInit {
  @Output() technicalEvaluationDataEmitter: EventEmitter<any> =
    new EventEmitter<any>();
  @Input() ITTDeveloperData: any = [];
  totalWeight:number = 0;
  projectName: string;
  projectId: number;

  closeModal = true;
  understandingOfScopeAndObjectivesControl = new FormControl(
    '',
    Validators.required
  );
  proposedMethodologyAndApproachControl = new FormControl(
    '',
    Validators.required
  );
  teamAndResourceCapabilityControl = new FormControl('', Validators.required);
  qualityAssuranceAndRiskManagementControl = new FormControl(
    '',
    Validators.required
  );
  addedValueAndSustainabilityControl = new FormControl('', Validators.required);

  constructor(
    private IttWrapperComponent: IttWrapperComponent,
    private router: Router,
    private TenderingService: TenderingService,
    private toast: NgToastService,
    private route: ActivatedRoute
  ) {}
  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.projectName = params['projectName'];
    });
    this.route.paramMap.subscribe((params) => {
      this.projectId = params.get('projectId')
        ? parseInt(params.get('projectId'))
        : null;
    });

    /**
     * NOTE: On edit or when we go back and forth between wizards, we will
     *  set initial value if it exists
     */
    if (
      this.ITTDeveloperData?.technical_evaluation?.scope_and_objectives &&
      this.ITTDeveloperData?.technical_evaluation?.methodology_and_approach &&
      this.ITTDeveloperData?.technical_evaluation?.team_and_resource &&
      this.ITTDeveloperData?.technical_evaluation?.quality_and_risk &&
      this.ITTDeveloperData?.technical_evaluation?.value_and_sustainability
    ) {
      this.understandingOfScopeAndObjectivesControl.setValue(
        this.ITTDeveloperData?.technical_evaluation?.scope_and_objectives
      );
      this.proposedMethodologyAndApproachControl.setValue(
        this.ITTDeveloperData?.technical_evaluation?.methodology_and_approach
      );
      this.teamAndResourceCapabilityControl.setValue(
        this.ITTDeveloperData?.technical_evaluation?.team_and_resource
      );
      this.qualityAssuranceAndRiskManagementControl.setValue(
        this.ITTDeveloperData?.technical_evaluation?.quality_and_risk
      );
      this.addedValueAndSustainabilityControl.setValue(
        this.ITTDeveloperData?.technical_evaluation?.value_and_sustainability
      );
    }
    this.understandingOfScopeAndObjectivesControl.valueChanges.subscribe(() => this.calculateTotal());
    this.proposedMethodologyAndApproachControl.valueChanges.subscribe(() => this.calculateTotal());
    this.teamAndResourceCapabilityControl.valueChanges.subscribe(() => this.calculateTotal());
    this.qualityAssuranceAndRiskManagementControl.valueChanges.subscribe(() => this.calculateTotal());
    this.addedValueAndSustainabilityControl.valueChanges.subscribe(() => this.calculateTotal());

    // // Calculate initial total
    this.calculateTotal();
  }
  calculateTotal(): void {
    this.totalWeight =
      Number(this.understandingOfScopeAndObjectivesControl.value) +
      Number(this.proposedMethodologyAndApproachControl.value) +
      Number(this.teamAndResourceCapabilityControl.value) +
      Number(this.qualityAssuranceAndRiskManagementControl.value) +
      Number(this.addedValueAndSustainabilityControl.value);
  }

  
  prepareDataToSubmit() {
    // Mark controls as touched to trigger validation UI
    this.understandingOfScopeAndObjectivesControl.markAsTouched();
    this.proposedMethodologyAndApproachControl.markAsTouched();
    this.teamAndResourceCapabilityControl.markAsTouched();
    this.qualityAssuranceAndRiskManagementControl.markAsTouched();
    this.addedValueAndSustainabilityControl.markAsTouched();

    // Check if all required fields have values
    if (
      this.understandingOfScopeAndObjectivesControl.value &&
      this.proposedMethodologyAndApproachControl.value &&
      this.teamAndResourceCapabilityControl.value &&
      this.qualityAssuranceAndRiskManagementControl.value &&
      this.addedValueAndSustainabilityControl.value
    ) {
      const evaluationCriteria = {
        technical_evaluation_criteria: [
          {
            name: 'Understanding of Scope and Objectives',
            weight: this.understandingOfScopeAndObjectivesControl.value,
          },
          {
            name: 'Proposed Methodology and Approach',
            weight: this.proposedMethodologyAndApproachControl.value,
          },
          {
            name: 'Team and Resource Capability',
            weight: this.teamAndResourceCapabilityControl.value,
          },
          {
            name: 'Quality Assurance and Risk Management',
            weight: this.qualityAssuranceAndRiskManagementControl.value,
          },
          {
            name: 'Added Value and Sustainability',
            weight: this.addedValueAndSustainabilityControl.value,
          },
        ],
      };
      this.calculateTotal();
      if (this.totalWeight !== 100) {
        this.toast.danger(
          `Total weight must be 100, but got ${this.totalWeight}`,
          'DANGER',
          4000
        );
        console.error(`Total weight must be 100, but got ${this.totalWeight}`);
        return false;
      }

      return evaluationCriteria;
    }
  }

  moveToNextStep() {
    const dataToSubmit = this.prepareDataToSubmit();

    if (dataToSubmit) {
      this.TenderingService.storeTechnicalEvaluationCriteria(
        dataToSubmit,
        this.projectId
      ).subscribe({
        next: (response: any) => {
          if (response?.meta?.success) {
            this.toast.success(
              'Evaluation criteria submitted successfully',
              'SUCCESS'
            );
            this.technicalEvaluationDataEmitter.emit(response?.data);
            this.IttWrapperComponent.nextStep();
            return;
          } else {
            this.toast.danger(
              'Evaluation criteria could not be saved',
              'DANGER',
              4000
            );
          }
        },
        error: (error: any) => {
          console.error('Failed to submit:', error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    }
  }

  moveToPreviousStep() {
    this.IttWrapperComponent.previousStep();
  }
}
